import axios from "axios";
import React, { useState } from "react";
import { useContext } from "react";
import { MdEmail, MdLock } from "react-icons/md";
import { Link, useNavigate } from "react-router-dom";
import logo from "../assessts/premier-logo.png";
import { API_URL } from "../config";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { AuthContext } from "../Context/AuthContext";
import "./Login.css";

function Login() {
  const [formData, setFormData] = useState({
    official_email: "",
    password: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isLocked, setIsLocked] = useState(false);

  const navigate = useNavigate();
  const { login } = useContext(AuthContext);

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const reason = params.get("reason");
    const lockedParam = params.get("locked");
    if (reason) {
      setError(reason);
    } else if (lockedParam === "true") {
      setError("Account locked due to inactivity. Please enter the unlock code from Super Admin.");
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      // 🔍 Step 3: API call
      const res = await axios.post(`${API_URL}/api/userLogin`, formData);

      // 🔍 Step 4: Check response data
      console.log("Response Data:", res.data);

      const {
        token,
        employeeId,
        ename,
        official_email,
        attendanceStatus,
        check_in,
      } = res.data;

      // 🔍 Step 6: Validate response
      if (!token || !employeeId) {
        console.error("Invalid login response:", res.data);
        throw new Error("Invalid login response");
      }

      console.log("Token:", token);
      console.log("Employee ID:", employeeId);

      // ✅ Local storage
      localStorage.setItem("token", token);
      localStorage.setItem(
        "user",
        JSON.stringify({ employeeId, ename, official_email })
      );
      localStorage.setItem("chatUserId", employeeId);
      localStorage.setItem("chatRole", res.data.role || "employee");
      localStorage.setItem("chatName", ename || "Employee");

      // 🔍 Step 7: Context login check
      console.log("Calling login context...");

      login({
        token,
        user: {
          _id: employeeId,
          name: ename || "Employee",
          role: res.data.role || "employee",
          userType: res.data.userType || "employee",
          email: official_email || "",
          permissions: {},
        },
      });

      // 🔍 Step 8: Attendance check
      if (check_in) {
        console.log("Check-in time:", check_in);
        localStorage.setItem("loginTime", check_in);
      }

      if (attendanceStatus !== "Present") {
        console.log("Marking attendance...");

        try {
          await axios.post(
            `${API_URL}/api/markAttendance`,
            { employeeId },
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
        } catch (attErr) {
          console.error("Attendance Error:", attErr);
        }
      }

      // 🔍 Step 9: Navigation
      console.log("Navigating to /employee");

      navigate("/employee");

    } catch (err) {
      console.error("Login Error:", err);
      if (err.response?.status === 423) {
        setIsLocked(true);
        setError("Account locked due to inactivity. Please enter the unlock code from Super Admin.");
      } else {
        setError(err.response?.data?.message || "Login failed");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-split">
        <section className="login-left-panel">
          <div className="login-left-content">
            <img src={logo} alt="Premier Logo" className="login-left-logo" />
            <h1>Welcome, Premier Webtech Solutions</h1>
            <p>Your trusted partner for IT and Digital Marketing solutions.</p>
          </div>
        </section>

        <section className="login-right-panel">
          <div className="login-card">
            <div className="login-card-head">
              <img src={logo} alt="Premier Logo" className="login-card-logo" />
              <h2>Staff Login</h2>
            </div>

            {error && <div className="alert alert-danger">{error}</div>}

            <form onSubmit={handleSubmit}>
              <div className="input-group mb-3">
                <span className="input-group-text login-input-icon">
                  <MdEmail />
                </span>
                <input
                  type="email"
                  name="official_email"
                  className="form-control login-input"
                  placeholder="Email"
                  value={formData.official_email}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="input-group mb-3 position-relative">
                <span className="input-group-text login-input-icon">
                  <MdLock />
                </span>
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  className="form-control login-input"
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                />
                <span className="login-eye" onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </span>
              </div>

              {isLocked && (
                <div className="input-group mb-3">
                  <span className="input-group-text login-input-icon">
                    <MdLock />
                  </span>
                  <input
                    type="text"
                    name="unlockOTP"
                    className="form-control login-input"
                    placeholder="Enter Unlock Code"
                    value={formData.unlockOTP || ""}
                    onChange={handleChange}
                    required
                  />
                </div>
              )}

              <div className="text-center mb-2 mt-3">
                <Link to="/forgot-password" className="login-forgot-link">Forgot Password?</Link>
              </div>

              <button type="submit" className="btn login-submit-btn w-100" disabled={loading}>
                {loading ? "Logging in..." : "Login"}
              </button>
            </form>

            <div className="text-center mb-1 mt-3">
              <button
                type="button"
                className="btn login-admin-btn px-4"
                onClick={() => navigate("/adminLogin")}
              >
                Admin Login
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

export default Login;
